// ═══════════════════════════════════════════════════════════════
// MASKS UTILITY — PeticionadorBR
// Mascaramento de campos de entrada. Armazena dígitos puros,
// exibe formatado. Antes de enviar à API: usar unmask() / parseBRL().
// ═══════════════════════════════════════════════════════════════

/** Extrai apenas dígitos de uma string */
export function digits(v) {
  return String(v ?? '').replace(/\D/g, '');
}

/** Alias para digits — usado para "desmascarar" antes de enviar à API */
export const unmask = digits;

// ── CPF: 000.000.000-00 ───────────────────────────────────────
export function maskCpf(v) {
  const d = digits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

// ── CNPJ: 00.000.000/0000-00 ─────────────────────────────────
export function maskCnpj(v) {
  const d = digits(v).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

// ── CEP: 00000-000 ────────────────────────────────────────────
export function maskCep(v) {
  const d = digits(v).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

// ── PIS/NIS: 000.00000.00-0 ───────────────────────────────────
export function maskPis(v) {
  const d = digits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 8) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8, 10)}-${d.slice(10, 11)}`;
}

// ── Telefone: (00) 00000-0000 ou (00) 0000-0000 ───────────────
export function maskPhone(v) {
  const d = digits(v).slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  // celular (11 dígitos) ou fixo (10)
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
}

// ── CEI: 000.000.000/00 ───────────────────────────────────────
export function maskCei(v) {
  const d = digits(v).slice(0, 12);
  if (d.length <= 3) return d;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 9)}/${d.slice(9)}`;
}

// ── Moeda BRL ─────────────────────────────────────────────────

/**
 * Formata um número ou string decimal para exibição pt-BR.
 * Ex: "3405.50" → "3.405,50"
 *     1234     → "1.234,00"
 */
export function formatBRL(v) {
  if (v === '' || v === null || v === undefined) return '';
  const raw = String(v).replace(',', '.');
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Converte string de input pt-BR para decimal string para API.
 * Ex: "3.405,50" → "3405.50"
 *     "3405,50"  → "3405.50"
 */
export function parseBRL(v) {
  if (!v && v !== 0) return '';
  return String(v)
    .replace(/\./g, '')   // remove separador de milhar
    .replace(',', '.');   // converte decimal
}

/**
 * Formata data ISO ou YYYY-MM-DD para dd/mm/yyyy.
 */
export function formatDate(v) {
  if (!v) return '';
  const s = String(v).split('T')[0];
  const parts = s.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    if (!y || !m || !d) return v;
    return `${d}/${m}/${y}`;
  }
  if (parts.length === 2) {
    const [y, m] = parts;
    if (!y || !m) return v;
    return `${m}/${y}`;
  }
  return v;
}

function isValidDateParts(day, month, year) {
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!d || !m || !y || y < 1900 || y > 2100 || m < 1 || m > 12) return false;
  return d <= new Date(y, m, 0).getDate();
}

function isValidMonthYearParts(month, year) {
  const m = Number(month);
  const y = Number(year);
  return m >= 1 && m <= 12 && y >= 1900 && y <= 2100;
}

/**
 * Máscara de data no padrão brasileiro: dd/mm/aaaa ou mm/aaaa.
 * Aceita valores ISO já salvos e os exibe no padrão pt-BR.
 */
export function maskDateBR(v) {
  if (!v) return '';
  const text = String(v).trim();

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return formatDate(text);
  }

  // YYYY-MM (apenas mês e ano)
  if (/^\d{4}-\d{2}$/.test(text)) {
    return formatDate(text);
  }

  // MM/YYYY já formatado
  if (/^\d{2}\/\d{4}$/.test(text)) {
    return text;
  }

  // Mantém anos isolados vindos de extração/legado para não virar "20/18".
  if (/^\d{4}$/.test(text)) {
    return text;
  }

  const d = digits(text).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  if (d.length <= 6 && (text.includes('/') && text.indexOf('/') === 2 && text.length <= 7)) {
    // Caso de digitação mm/aaaa: '09/2023'
    return `${d.slice(0, 2)}/${d.slice(2, 6)}`;
  }
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Converte input dd/mm/aaaa válido para ISO yyyy-mm-dd,
 * ou mm/aaaa para ISO yyyy-mm.
 * Enquanto incompleto ou inválido, preserva a máscara digitada.
 */
export function normalizeDateBRInput(v) {
  const masked = maskDateBR(v);
  
  // DD/MM/YYYY
  const matchFull = masked.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (matchFull) {
    const [, day, month, year] = matchFull;
    if (isValidDateParts(day, month, year)) {
      return `${year}-${month}-${day}`;
    }
    return masked;
  }

  // MM/YYYY
  const matchMonthYear = masked.match(/^(\d{2})\/(\d{4})$/);
  if (matchMonthYear) {
    const [, month, year] = matchMonthYear;
    if (isValidMonthYearParts(month, year)) {
      return `${year}-${month}`;
    }
  }

  return masked;
}

/**
 * Formata datetime ISO para dd/mm/yyyy hh:mm.
 */
export function formatDateTime(v) {
  if (!v) return '';
  const [datePart, timePart = ''] = String(v).split('T');
  const [y, m, d] = datePart.split('-');
  const time = timePart.slice(0, 5);
  if (!y || !m || !d) return v;
  return time ? `${d}/${m}/${y} ${time}` : `${d}/${m}/${y}`;
}

/**
 * Data de hoje em AAAA-MM-DD segundo o fuso de São Paulo.
 *
 * `new Date().toISOString().split('T')[0]` devolve a data em UTC: às 21h30 no Brasil já é o dia
 * seguinte lá, e a ficha nascia com a data da entrevista errada toda noite. `sv-SE` é usado por
 * produzir exatamente AAAA-MM-DD, que é o formato que a API espera.
 */
export function hojeISO(fuso = 'America/Sao_Paulo') {
  return new Date().toLocaleDateString('sv-SE', { timeZone: fuso });
}
